import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { LeaderboardTable } from "../components/LeaderboardTable";
import { api } from "../lib/api";
import { formatFullDate } from "../lib/format";
import type { TeamDetail } from "../lib/types";

export function TeamDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const teamId = id ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const me = useQuery({ queryKey: ["me"], queryFn: api.me });

  const team = useQuery({
    queryKey: ["team", teamId],
    queryFn: () => api.getTeam(teamId),
  });

  const board = useQuery({
    queryKey: ["team-leaderboard", teamId],
    queryFn: () => api.teamLeaderboard(teamId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["team", teamId] });

  const publish = useMutation({
    mutationFn: (value: boolean) =>
      api.updateTeam(teamId, { leaderboard_public: value }),
    onSuccess: invalidate,
  });

  const invite = useMutation({
    mutationFn: () => api.createInvite(teamId),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (userId: string) => api.removeTeamMember(teamId, userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
      await invalidate();
    },
  });

  const destroy = useMutation({
    mutationFn: () => api.deleteTeam(teamId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["teams"] });
      navigate("/teams");
    },
  });

  if (team.isPending) {
    return <p className="text-sm text-zinc-500">{t("team.loading")}</p>;
  }
  if (team.isError || !team.data) {
    return <p className="text-sm text-red-400">{t("team.notFound")}</p>;
  }

  const detail: TeamDetail = team.data;
  const isOwner = detail.role === "owner";

  return (
    <div>
      <Link to="/teams" className="text-sm text-zinc-500 hover:text-zinc-300">
        ← {t("team.back")}
      </Link>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{detail.name}</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t("teams.drivers", { count: detail.member_count })} ·{" "}
            {t(detail.leaderboard_public ? "team.boardPublic" : "team.boardPrivate")}
          </p>
        </div>

        {isOwner && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => publish.mutate(!detail.leaderboard_public)}
              disabled={publish.isPending}
              className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-zinc-500 hover:text-zinc-100 disabled:opacity-50"
            >
              {t(detail.leaderboard_public ? "team.makePrivate" : "team.publish")}
            </button>
            <DeleteTeamButton onConfirm={() => destroy.mutate()} />
          </div>
        )}
      </div>

      {isOwner && (
        <Invites
          detail={detail}
          onCreate={() => invite.mutate()}
          creating={invite.isPending}
          onRevoke={(token) =>
            api.revokeInvite(teamId, token).then(() => {
              void invalidate();
            })
          }
        />
      )}

      <section className="mt-8">
        <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
          {t("team.drivers")}
        </h2>
        <div className="mt-4 overflow-hidden rounded-xl border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3 font-medium">{t("team.columns.driver")}</th>
                <th className="px-4 py-3 font-medium">{t("team.columns.role")}</th>
                <th className="px-4 py-3 text-right font-medium">
                  {t("team.columns.sharedSessions")}
                </th>
                <th className="px-4 py-3">
                  <span className="sr-only">{t("team.columns.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {detail.members.map((member) => {
                const self = member.user_id === me.data?.id;
                return (
                  <tr key={member.user_id} className="hover:bg-zinc-900/40">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        {member.avatar_url && (
                          <img
                            src={member.avatar_url}
                            alt=""
                            className="size-6 rounded-full ring-1 ring-zinc-700"
                          />
                        )}
                        <span className="text-zinc-200">{member.persona_name}</span>
                        {self && (
                          <span className="text-xs text-zinc-500">{t("team.you")}</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-400">
                      {t(`team.roles.${member.role}`, { defaultValue: member.role })}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                      {member.session_count}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {member.role !== "owner" && (self || isOwner) && (
                        <button
                          type="button"
                          onClick={() => remove.mutate(member.user_id)}
                          className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-400 transition-colors hover:border-red-800 hover:text-red-400"
                        >
                          {t(self ? "team.leave" : "team.remove")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
            {t("team.board")}
          </h2>
          <span className="text-xs text-zinc-600">{t("team.boardHint")}</span>
        </div>

        {board.isPending ? (
          <p className="mt-4 text-sm text-zinc-500">{t("leaderboard.loading")}</p>
        ) : board.isError ? (
          <p className="mt-4 text-sm text-red-400">{t("leaderboard.error")}</p>
        ) : board.data.length === 0 ? (
          <div className="mt-4 rounded-xl border border-zinc-800 px-6 py-12 text-center text-sm text-zinc-400">
            {t("team.boardEmpty")}
          </div>
        ) : (
          <div className="mt-4">
            <LeaderboardTable
              entries={board.data}
              comparePath={`/teams/${teamId}/compare`}
              currentUserId={me.data?.id}
            />
          </div>
        )}
      </section>
    </div>
  );
}

interface InvitesProps {
  detail: TeamDetail;
  onCreate: () => void;
  creating: boolean;
  onRevoke: (token: string) => void;
}

function Invites({ detail, onCreate, creating, onRevoke }: InvitesProps) {
  const { t } = useTranslation();

  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-medium uppercase tracking-wider text-zinc-500">
          {t("team.invites")}
        </h2>
        <button
          type="button"
          onClick={onCreate}
          disabled={creating}
          className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400 disabled:opacity-40"
        >
          {creating ? t("teams.creating") : t("team.newInvite")}
        </button>
      </div>

      {detail.invites.length === 0 ? (
        <p className="mt-4 text-sm text-zinc-500">{t("team.noInvites")}</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {detail.invites.map((entry) => (
            <li key={entry.token} className="flex items-center gap-2">
              <CopyableLink url={`${window.location.origin}/join/${entry.token}`} />
              <span className="text-xs text-zinc-600">
                {entry.expires_at
                  ? t("team.expires", { date: formatFullDate(entry.expires_at) })
                  : t("team.noExpiry")}
              </span>
              <button
                type="button"
                onClick={() => onRevoke(entry.token)}
                className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-400 transition-colors hover:border-red-800 hover:text-red-400"
              >
                {t("team.revoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CopyableLink({ url }: { url: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(url).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1_500);
        });
      }}
      className="flex-1 truncate rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-left font-mono text-xs text-zinc-400 transition-colors hover:border-zinc-600 hover:text-zinc-200"
      title={t("team.copy")}
    >
      {copied ? t("team.copied") : url}
    </button>
  );
}

function DeleteTeamButton({ onConfirm }: { onConfirm: () => void }) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs text-zinc-400 transition-colors hover:border-red-800 hover:text-red-400"
      >
        {t("team.delete")}
      </button>
    );
  }

  return (
    <span className="flex items-center gap-2 text-xs">
      <button
        type="button"
        onClick={onConfirm}
        className="rounded-md bg-red-900/60 px-2 py-1 font-medium text-red-100 transition-colors hover:bg-red-800"
      >
        {t("team.deleteConfirm")}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="text-zinc-500 transition-colors hover:text-zinc-300"
      >
        {t("team.cancel")}
      </button>
    </span>
  );
}

